import { Button } from "@package/subframe/components/Button";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { FeatherRotateCcw } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useReopenTaskInstanceCommandControllerReopen } from "@/generated/compliance-tasks/compliance-tasks";
import type { ComplianceTaskInstanceResponseDto } from "@/generated/model";
import {
  extractApiErrorMessage,
  invalidateComplianceQueries,
} from "@/lib/compliance/invalidate-compliance-queries";

interface ReopenInstanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instance: ComplianceTaskInstanceResponseDto;
}

export function ReopenInstanceDialog({
  open,
  onOpenChange,
  instance,
}: ReopenInstanceDialogProps) {
  const queryClient = useQueryClient();
  const reopen = useReopenTaskInstanceCommandControllerReopen();
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await reopen.mutateAsync({ instanceId: instance.id });
      toast.success("Task reopened — it now shows as overdue until completed");
      await invalidateComplianceQueries(queryClient);
      onOpenChange(false);
    } catch (error: unknown) {
      toast.error(extractApiErrorMessage(error, "Failed to reopen the task"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
        <DialogTitle className="sr-only">
          Reopen compliance task instance
        </DialogTitle>
        <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white">
          <div className="flex max-w-[448px] flex-col items-center gap-6 p-4">
            <div className="flex flex-col items-center gap-6">
              <IconWithLogoBackground
                variant="warning"
                size="x-large"
                icon={<FeatherRotateCcw />}
              />
              <div className="flex flex-col items-center gap-2">
                <span className="w-full text-center text-heading-2 font-heading-2 text-default-font">
                  Reopen this task?
                </span>
                <span className="text-center text-body font-body text-subtext-color">
                  The completion of{" "}
                  <strong>
                    {format(new Date(instance.dueDate), "d MMM yyyy")}
                  </strong>{" "}
                  will be undone so it can be completed again. It will show as
                  overdue until re-completed, and existing answers and files are
                  kept so you can correct them. The next occurrence that this
                  completion scheduled will be removed and rescheduled when you
                  complete it again.
                </span>
              </div>
            </div>
          </div>
          <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4">
            <Button
              className="h-14 w-80 flex-none"
              variant="neutral-secondary"
              size="extra-large"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              className="h-14 w-80 flex-none"
              variant="neutral-primary"
              size="extra-large"
              onClick={handleConfirm}
              disabled={busy}
            >
              {busy ? "Reopening..." : "Yes, reopen"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
